import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';

const features = [
  { icon: '🤖', title: 'AI Powered Chatbot', desc: 'Our intelligent chatbot understands customer questions and provides relevant responses automatically — 24/7 without human involvement.' },
  { icon: '⚡', title: '24/7 Customer Support', desc: 'Your website visitors get instant answers anytime, even outside business hours. Never miss a customer query again.' },
  { icon: '🎯', title: 'Lead Generation', desc: 'Capture customer details such as name, phone number, and email directly through chat conversations seamlessly.' },
  { icon: '🔌', title: 'Easy Website Integration', desc: 'MS Chat integrates into your website with a single line of code. No technical knowledge required.' },
  { icon: '⚙️', title: 'Business Automation', desc: 'Reduce manual workload by automating frequently asked questions and support queries with smart AI responses.' },
  { icon: '📊', title: 'Analytics & Insights', desc: 'Track visitor location, device, traffic source and chat performance with detailed analytics dashboards.' }
];

const steps = [
  { num: '01', title: 'Register & Choose Plan', desc: 'Sign up with your company details and select the plan that fits your business needs.' },
  { num: '02', title: 'Make Payment', desc: 'Transfer payment to our bank account and submit your transaction ID for quick verification.' },
  { num: '03', title: 'Set Up Your Chat', desc: 'Add your logo, brand color, agents and configure your chat widget settings easily.' },
  { num: '04', title: 'Go Live', desc: 'Copy your unique script tag, paste it on your website and start chatting instantly.' }
];

const plans = [
  { name: 'Pack One',      key: 'pack_one',      usd: '$99',  inr: '₹5,200',  period: '/month', agents: '2 Agents',       chats: '2,000/month',  websites: '5 Websites',  ai: false, api: 'Read Only',  featured: false },
  { name: 'Pack Two',      key: 'pack_two',      usd: '$149', inr: '₹7,499',  period: '/month', agents: '5 Agents',       chats: '20,000/month', websites: '10 Websites', ai: false, api: 'Read Only',  featured: true  },
  { name: 'Pack Advanced', key: 'pack_advanced', usd: '$299', inr: '₹11,999', period: '/month', agents: '20 Agents + AI', chats: 'Unlimited',    websites: 'Unlimited',   ai: true,  api: 'Full Access', featured: false }
];

export default function HomePage() {
  const isIndia = Intl.DateTimeFormat().resolvedOptions().timeZone === 'Asia/Calcutta' ||
                  Intl.DateTimeFormat().resolvedOptions().timeZone === 'Asia/Kolkata' ||
                  navigator.language === 'en-IN';
  const currency = isIndia ? 'inr' : 'usd'; // Auto-detect, no manual toggle

  return (
    <div style={{ background: '#000', minHeight: '100vh' }}>
      <Navbar />

      {/* Hero */}
      <section style={{ padding: '80px 24px 60px', textAlign: 'center', maxWidth: 900, margin: '0 auto' }}>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'rgba(0,213,91,0.1)', border: '1px solid rgba(0,213,91,0.2)', borderRadius: 20, padding: '5px 14px', marginBottom: 24 }}>
          <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#00d55b', display: 'inline-block' }}></span>
          <span style={{ color: '#00d55b', fontSize: 12, fontWeight: 500 }}>Live Chat + AI Chatbot Platform</span>
        </div>
        <h1 className="hero-title" style={{ fontSize: 56, fontWeight: 700, lineHeight: 1.1, marginBottom: 20, letterSpacing: '-0.02em' }}>
          Smart AI Chatbot That<br />
          <span style={{ color: '#00d55b' }}>Works for Your Business</span><br />
          24/7
        </h1>
        <p style={{ fontSize: 17, color: '#888', maxWidth: 580, margin: '0 auto 36px', lineHeight: 1.7 }}>
          Welcome to MSChat by MS Discoveries — a smart and powerful chatbot platform designed to help businesses communicate with customers instantly and efficiently.
        </p>
        <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
          <Link to="/register" style={{ padding: '13px 30px', background: '#00d55b', color: '#000', borderRadius: 10, fontSize: 15, fontWeight: 700 }}>
            Start Free Today →
          </Link>
          <Link to="/plans" style={{ padding: '13px 30px', border: '1px solid #1e1e1e', color: '#fff', borderRadius: 10, fontSize: 15, fontWeight: 500 }}>
            View Plans
          </Link>
        </div>

        {/* Stats */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1, marginTop: 56, background: '#1e1e1e', borderRadius: 12, overflow: 'hidden' }}>
          {[['24/7', 'Always Online'], ['1 Line', 'Easy Setup'], ['3 Plans', 'For Every Business']].map(([val, label]) => (
            <div key={val} style={{ background: '#0d0d0d', padding: '20px', textAlign: 'center' }}>
              <div style={{ fontSize: 26, fontWeight: 700, color: '#00d55b' }}>{val}</div>
              <div style={{ fontSize: 12, color: '#555', marginTop: 4 }}>{label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Why Choose */}
      <section style={{ padding: '70px 24px', background: '#0d0d0d', borderTop: '1px solid #1e1e1e', borderBottom: '1px solid #1e1e1e' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 50 }}>
            <h2 style={{ fontSize: 34, fontWeight: 700, marginBottom: 12 }}>Why Choose <span style={{ color: '#00d55b' }}>MSChat?</span></h2>
            <p style={{ color: '#888', fontSize: 15, maxWidth: 480, margin: '0 auto' }}>In today's digital world, customers expect quick responses and continuous support.</p>
          </div>
          <div className="feature-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 16 }}>
            {features.map(f => (
              <div key={f.title} style={{ background: '#111', border: '1px solid #1e1e1e', borderRadius: 12, padding: 22, cursor: 'default', transition: 'border-color 0.2s' }}
                onMouseEnter={e => e.currentTarget.style.borderColor = '#00d55b'}
                onMouseLeave={e => e.currentTarget.style.borderColor = '#1e1e1e'}>
                <div style={{ fontSize: 26, marginBottom: 10 }}>{f.icon}</div>
                <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 8 }}>{f.title}</h3>
                <p style={{ color: '#666', fontSize: 13, lineHeight: 1.7 }}>{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section style={{ padding: '70px 24px' }}>
        <div style={{ maxWidth: 900, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 50 }}>
            <h2 style={{ fontSize: 34, fontWeight: 700, marginBottom: 12 }}>How It <span style={{ color: '#00d55b' }}>Works</span></h2>
            <p style={{ color: '#888', fontSize: 15 }}>Get your chat live in under 10 minutes</p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 20 }}>
            {steps.map((s, i) => (
              <div key={s.num} style={{ textAlign: 'center', padding: '16px 8px' }}>
                <div style={{ width: 52, height: 52, borderRadius: '50%', background: 'rgba(0,213,91,0.1)', border: '1px solid rgba(0,213,91,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px', fontSize: 15, fontWeight: 700, color: '#00d55b' }}>{s.num}</div>
                <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>{s.title}</h3>
                <p style={{ color: '#555', fontSize: 12, lineHeight: 1.6 }}>{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Plans Preview */}
      <section style={{ padding: '70px 24px', background: '#0d0d0d', borderTop: '1px solid #1e1e1e', borderBottom: '1px solid #1e1e1e' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 40 }}>
            <h2 style={{ fontSize: 34, fontWeight: 700, marginBottom: 12 }}>Simple, Transparent <span style={{ color: '#00d55b' }}>Pricing</span></h2>
          </div>
          <div className="plans-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
            {plans.map(p => (
              <div key={p.name} style={{ background: '#111', border: `1px solid ${p.featured ? '#00d55b' : '#1e1e1e'}`, borderRadius: 16, padding: 24, position: 'relative', display: 'flex', flexDirection: 'column', gap: 14 }}>
                {p.featured && <div style={{ position: 'absolute', top: -12, left: '50%', transform: 'translateX(-50%)', background: '#00d55b', color: '#000', fontSize: 11, fontWeight: 700, padding: '2px 14px', borderRadius: 20 }}>Most Popular</div>}
                {p.ai && <div style={{ display: 'inline-block', background: 'rgba(0,213,91,0.1)', color: '#00d55b', fontSize: 11, padding: '2px 10px', borderRadius: 20, width: 'fit-content' }}>AI Powered</div>}
                <h3 style={{ fontSize: 18, fontWeight: 700 }}>{p.name}</h3>
                <div style={{ fontSize: 34, fontWeight: 700, color: '#00d55b', lineHeight: 1 }}>
                  {currency === 'usd' ? p.usd : p.inr}
                  <span style={{ fontSize: 13, color: '#555', fontWeight: 400 }}>{p.period}</span>
                </div>
                <div style={{ color: '#888', fontSize: 13, lineHeight: 1.9 }}>
                  <div>👥 {p.agents}</div>
                  <div>💬 {p.chats}</div>
                </div>
                <Link to="/register" style={{ display: 'block', textAlign: 'center', padding: '10px', background: p.featured ? '#00d55b' : 'transparent', color: p.featured ? '#000' : '#00d55b', border: `1px solid #00d55b`, borderRadius: 8, fontSize: 14, fontWeight: 700, marginTop: 'auto' }}>
                  Get Started →
                </Link>
              </div>
            ))}
          </div>
          <div style={{ textAlign: 'center', marginTop: 20 }}>
            <Link to="/plans" style={{ color: '#00d55b', fontSize: 14 }}>See full feature comparison →</Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={{ padding: '70px 24px', textAlign: 'center' }}>
        <div style={{ maxWidth: 560, margin: '0 auto' }}>
          <h2 style={{ fontSize: 34, fontWeight: 700, marginBottom: 16 }}>Ready to <span style={{ color: '#00d55b' }}>Get Started?</span></h2>
          <p style={{ color: '#888', fontSize: 15, marginBottom: 28, lineHeight: 1.7 }}>
            Join businesses already using MSChat to communicate with their customers smarter and faster.
          </p>
          <Link to="/register" style={{ display: 'inline-block', padding: '13px 36px', background: '#00d55b', color: '#000', borderRadius: 10, fontSize: 15, fontWeight: 700 }}>
            Start Using MSChat Today →
          </Link>
          <p style={{ color: '#333', fontSize: 12, marginTop: 14 }}>No credit card required · Setup in minutes</p>
        </div>
      </section>

      <Footer />
    </div>
  );
}